import { ZAttribute } from "src/types/ZAttribute";
import { ZRestriction } from "src/types/ZRestriction";

/**
 * Каскадная очистка атрибутов, зависящих от изменённых значений формы. LPLM-5173
 *
 * Виды зависимостей:
 * 1. Справочник, ограниченный по значению другого справочника (restrictions):
 *    из значения зависимого справочника убираются варианты, недопустимые
 *    для нового значения ограничивающего.
 * 2. Ссылка на объект, ограниченная по значению атрибута (attr.filter):
 *    значение ссылки очищается. Очистка кодируется пустым массивом [],
 *    а не undefined: ed2entity отбрасывает undefined при сериализации,
 *    и очистка не попадала бы в PATCH при сохранении.
 *
 * Очистка транзитивна: если каскад изменил значение атрибута, обрабатываются
 * и зависимые от него атрибуты. Атрибуты из changedKeys (изменённые
 * пользователем) каскад не трогает - они источник истины.
 */

/**
 * Допустимые значения ограничиваемого справочника для текущего значения
 * ограничивающего. Ограничение применимо только к одиночному значению,
 * иначе допустимых значений нет.
 */
export const getAllowedSourceIds = (
  restriction: ZRestriction,
  parentValue: unknown,
): Set<string> => {
  const single =
    Array.isArray(parentValue) && parentValue.length === 1
      ? String(parentValue[0])
      : undefined;
  if (!single) return new Set();
  return new Set(
    restriction.values
      .filter(({ restrictionId }) => String(restrictionId) === single)
      .map(({ sourceId }) => String(sourceId)),
  );
};

// Значения полей, которые нужно обновить в форме.
// "Пусто" - всегда [] (undefined не переживает сохранение, см. ed2entity)
export type CascadeUpdates = Record<string, string[]>;

type CascadeParams = {
  // Все атрибуты объекта (для поиска ссылок с filter)
  attributes: ZAttribute[];
  // Ограничения по значению: id ограничиваемого атрибута -> описание ограничения
  restrictions: ReadonlyMap<string, ZRestriction>;
  // Имена полей, изменённых пользователем
  changedKeys: string[];
  // Текущее значение поля формы
  getFieldValue: (name: string) => unknown;
  // Можно ли очищать атрибут. Должен совпадать с фильтром прав при сохранении
  // (Permission.attrUpdate в EntityCardStore.save): очистка атрибута, который
  // не попадёт в PATCH, бессмысленна - после сохранения значение вернётся.
  canClear?: (attrId: string) => boolean;
};

export const cascadeDependentAttrs = ({
  attributes,
  restrictions,
  changedKeys,
  getFieldValue,
  canClear,
}: CascadeParams): CascadeUpdates => {
  // Ссылки на объект, ограниченные по значению другого атрибута
  const linksByFilter = new Map<string, string[]>();
  attributes
    .filter(({ filter }) => !!filter)
    .forEach(({ id, filter }) => {
      const key = String(filter);
      linksByFilter.set(key, [...(linksByFilter.get(key) ?? []), String(id)]);
    });

  // Справочники, ограниченные по значению другого справочника
  const dictsByParent = new Map<
    string,
    { childId: string; restriction: ZRestriction }[]
  >();
  restrictions.forEach((restriction, childId) => {
    const key = String(restriction.restrictionAttributeId);
    dictsByParent.set(key, [
      ...(dictsByParent.get(key) ?? []),
      { childId, restriction },
    ]);
  });

  const updates: CascadeUpdates = {};
  // Значение поля с учётом уже сделанных каскадом изменений
  const currentValue = (key: string): unknown =>
    key in updates ? updates[key] : getFieldValue(key);

  const clearable = (attrId: string): boolean => !canClear || canClear(attrId);

  const origin = new Set(changedKeys);
  const queue = [...changedKeys];
  const visited = new Set(changedKeys);
  const enqueue = (id: string) => {
    if (!visited.has(id)) {
      visited.add(id);
      queue.push(id);
    }
  };

  while (queue.length) {
    const key = queue.shift()!;
    dictsByParent.get(key)?.forEach(({ childId, restriction }) => {
      if (origin.has(childId) || !clearable(childId)) return;
      const childValue = currentValue(childId);
      if (!Array.isArray(childValue) || childValue.length === 0) return;
      const allowed = getAllowedSourceIds(restriction, currentValue(key));
      const next = childValue
        .map(String)
        .filter((singleValue) => allowed.has(singleValue));
      if (next.length !== childValue.length) {
        updates[childId] = next;
        enqueue(childId);
      }
    });
    linksByFilter.get(key)?.forEach((linkId) => {
      if (origin.has(linkId) || !clearable(linkId)) return;
      const cur = currentValue(linkId);
      const isEmpty =
        cur === undefined ||
        cur === null ||
        (Array.isArray(cur) && cur.length === 0);
      if (isEmpty) return;
      updates[linkId] = [];
      enqueue(linkId);
    });
  }
  return updates;
};
